<template>
  <div class="v3-form detentions-form">
    <div class="row mb-3">
      <Label
        required
        label-id="td-cal-detention-lbl"
        html-for="detention-calendar-form-detention"
        >Detention</Label
      >
      <CustomSelect
        v-model="state.form.selectedDetention"
        input-id="detention-calendar-form-detention"
        accessible-label="Detention"
        labelled-by="td-cal-detention-lbl"
        hide-floating-label
        autocomplete="off"
        :filterable="true"
        :close-on-select="true"
        :options="state.detentions"
        :disabled="editableDetention !== null"
        :placeholder="$t('common.select_one')"
        @update:model-value="onChangeSelectedDetention"
        :invalid-feedback="
          errors.selectedDetention || state.serverErrors.selectedDetention
        "
        :class="{
          'col-md-10': editableDetention
        }"
      ></CustomSelect>
      <IconButton
        v-if="editableDetention"
        icon="ri-delete-bin-line"
        aria-label="Remove detention"
        @click="onDelete"
      />
    </div>

    <div class="row mb-3" v-if="state.form.selectedDetention">
      <div class="col-md-4">
        <Label required for="detention-calendar-form-start-time"
          >Start time</Label
        >
        <CustomTimePicker
          format="HH:mm:ss"
          input-id="detention-calendar-form-start-time"
          :invalid-feedback="
            errors.startTime || state.serverErrors.default_start_time
          "
          v-model="state.form.selectedDetention.default_start_time"
          readonly
        ></CustomTimePicker>
      </div>
      <div class="col-md-4">
        <Label required for="detention-calendar-form-room">Room</Label>
        <InputField
          id="detention-calendar-form-room"
          :invalid-feedback="errors.room || state.serverErrors.room"
          :rows="4"
          class="w-full"
          readonly
          v-model="state.form.selectedDetention.room.name"
        ></InputField>
      </div>
      <div class="col-md-4">
        <Label required for="detention-calendar-form-max-capacity"
          >Max Capacity</Label
        >
        <InputField
          id="detention-calendar-form-max-capacity"
          :invalid-feedback="errors.room || state.serverErrors.room"
          :rows="4"
          class="w-full"
          readonly
          v-model="state.form.selectedDetention.max_cap"
        ></InputField>
      </div>
    </div>
    <div
      v-if="!isUpdateMode"
      class="mb-3 d-flex align-items-center justify-content-between"
    >
      <Label class="my-0" for="schedule-detention-bulk-switch"
        >Bulk Schedule</Label
      >
      <CustomSwitch
        id="schedule-detention-bulk-switch"
        v-model="state.isBulkAddEnabled"
        test-id="schedule-detention-bulk-switch"
      />
    </div>
    <div class="mb-3">
      <div v-if="!state.isBulkAddEnabled">
        <Label required>Detention Date</Label>
        <CustomDatePicker
          v-model="state.form.detentionDate"
          :min-date="tomorrow"
          :max-date="maxSchoolYearDate"
          placeholder="Select Date"
          :disabled="selectedDate && !editableDetention"
          :invalid-feedback="
            errors.detentionDate || state.serverErrors.detentionDate
          "
        ></CustomDatePicker>
      </div>
      <div v-else>
        <div class="mb-3 d-flex align-items-center justify-content-between">
          <Label class="my-0" for="schedule-detention-mode-switch"
            >Range Mode</Label
          >
          <CustomSwitch
            id="schedule-detention-mode-switch"
            v-model="state.isRangeModeEnabled"
            test-id="schedule-detention-mode-switch"
          />
        </div>
        <span
          v-if="state.isDetentionDataLoading"
          class="visually-hidden"
          role="status"
          aria-live="polite"
          aria-atomic="true"
          >{{ $t("common.a11y_loading") }}</span
        >
        <div
          class="bulk-schedule-calendar-wrap"
          @focusin.capture="onBulkCalendarFocusIn"
        >
          <VueDatePicker
            v-if="state.isRangeModeEnabled"
            ref="bulkDetentionDatePickerRange"
            class="bulk-schedule-calendar"
            inline
            week-start="0"
            :range="{ noDisabledRange: true }"
            hide-offset-dates
            prevent-min-max-navigation
            :min-date="tomorrow"
            :max-date="maxSchoolYearDate"
            :disabled-dates="disabledDates"
            :loading="state.isDetentionDataLoading"
            :enable-time-picker="false"
            :action-row="{ showSelect: false, showPreview: false }"
            @internal-model-change="handleSelectedDates"
            @update-month-year="handleMonthChange"
          />
          <VueDatePicker
            v-else
            ref="bulkDetentionDatePickerMulti"
            class="bulk-schedule-calendar"
            inline
            week-start="0"
            multi-dates
            hide-offset-dates
            prevent-min-max-navigation
            :min-date="tomorrow"
            :max-date="maxSchoolYearDate"
            :disabled-dates="disabledDates"
            :loading="state.isDetentionDataLoading"
            :enable-time-picker="false"
            :action-row="{ showSelect: false, showPreview: false }"
            @internal-model-change="handleSelectedDates"
            @update-month-year="handleMonthChange"
          />
        </div>
      </div>
    </div>

    <div class="mt-4">
      <InfoBox
        v-if="state.serverRes"
        :class="{ danger: Object.keys(state.serverErrors).length }"
        :title="state.serverRes.title"
        :announce-assertive="
          Boolean(Object.keys(state.serverErrors).length) ||
          state.serverRes?.title === 'Success'
        "
        :message="state.serverRes.message"
      />
    </div>
    <div class="text-center">
      <LoadingButton class="me-2 px-4" @click="onCancel" rounded
        >Cancel</LoadingButton
      >
      <LoadingButton
        :is-loading="state.isProcessing"
        @click="submit(isUpdateMode)"
        class="mt-4 px-4"
        solid
        rounded
        >{{ isUpdateMode ? "Update" : "Add" }}</LoadingButton
      >
    </div>
  </div>
</template>

<script>
import {
  reactive,
  computed,
  onMounted,
  inject,
  watch,
  ref,
  nextTick,
  onUnmounted
} from "vue"
import { useStore } from "vuex"
import { helpers, required } from "@vuelidate/validators"
import useVuelidate from "@vuelidate/core"
import InputField from "@/v3components/shared/Form/InputField.vue"
import Label from "@/v3components/shared/Form/Label.vue"
import CustomSelect from "@/v3components/shared/Form/CustomSelect.vue"
import CustomSwitch from "@/v3components/shared/Form/CustomSwitch.vue"
import CustomTimePicker from "@/v3components/shared/Form/CustomTimePicker.vue"
import CustomDatePicker from "@/v3components/shared/Form/CustomDatePicker.vue"
import LoadingButton from "@/v3components/shared/Buttons/LoadingButton.vue"
import InfoBox from "@/v3components/shared/Alerts/InfoBox.vue"
import IconButton from "@/v3components/shared/Buttons/IconButton.vue"
import helpersJS from "@/helpers/index.js"
import moment from "moment-timezone"

export default {
  name: "CreateTardyDetentionCalendarForm",
  components: {
    InputField,
    Label,
    CustomTimePicker,
    LoadingButton,
    InfoBox,
    CustomDatePicker,
    CustomSwitch,
    CustomSelect,
    IconButton
  },
  props: {
    editableDetention: {
      type: Object,
      default: null
    },
    dailyDetentionsIds: {
      type: Array,
      default: null
    },
    selectedDate: {
      type: Object,
      default: null
    }
  },
  emits: ["cancel"],
  setup(props, { emit }) {
    const store = useStore()
    const actionDialog = inject("actionDialog")
    const state = reactive({
      detentions: [],
      isProcessing: false,
      serverErrors: {},
      serverRes: null,

      form: {
        selectedDetention: null,
        detentionDate: null
      },
      isBulkAddEnabled: false,
      isRangeModeEnabled: false,
      isDetentionDataLoading: false
    })
    onMounted(() => {
      if (props.selectedDate) {
        state.form.detentionDate = helpersJS.currTzDate(props.selectedDate)
      }

      if (props.editableDetention) {
        setFormData({
          selectedDetention: props.editableDetention,
          detentionDate: helpersJS.currTzDate(props.selectedDate)
        })
      } else {
        getDetentions()
      }
      resetMonthToCurrent()
      nextTick(() => {
        applyBulkCalendarA11yPatches()
        setupBulkCalendarObserver()
      })
    })

    const currentYear = new Date().getFullYear()
    const currentMonth = new Date().getMonth()
    const CALENDAR_ROOT_SELECTOR = ".dp__outer_menu_wrap"
    const CALENDAR_ROOT_FALLBACK_SELECTOR =
      ".dp__menu, .dp__main, .dp__instance_calendar"
    const CALENDAR_HEADER_SELECTOR = ".dp__calendar_header"
    const CALENDAR_HEADER_ITEM_SELECTOR = ".dp__calendar_header_item"
    /* Calendar header toggles use `data-test="month-toggle-overlay-0"` (no `-btn`),
       unlike the time-picker's `*-toggle-overlay-btn-0`. Match the calendar form so the
       accessible names actually get applied (Deque 2175407). */
    const CALENDAR_MONTH_TOGGLE_SELECTOR =
      'button[data-test*="month-toggle-overlay"]'
    const CALENDAR_YEAR_TOGGLE_SELECTOR =
      'button[data-test*="year-toggle-overlay"]'
    const CALENDAR_DAY_SELECTOR = ".dp__calendar_item"
    const CALENDAR_DAY_INNER_SELECTOR = ".dp__cell_inner"
    const CALENDAR_NAV_AND_TOGGLE_SELECTOR =
      ".dp__month_year_wrap button:not([disabled])"
    const CALENDAR_DISABLED_OR_OFFSET_SELECTOR = [
      ".dp__calendar_item.dp__cell_disabled",
      ".dp__calendar_item.dp__cell_offset",
      ".dp__calendar_item[aria-disabled='true']",
      ".dp__cell_inner.dp__cell_disabled",
      ".dp__cell_inner.dp__cell_offset",
      ".dp__cell_inner[aria-disabled='true']"
    ].join(", ")
    const CALENDAR_ENABLED_FOCUS_TARGET_SELECTOR = [
      ".dp__calendar_item:not(.dp__cell_disabled):not(.dp__cell_offset) [tabindex='0']",
      ".dp__calendar_item:not(.dp__cell_disabled):not(.dp__cell_offset) .dp__cell_inner",
      ".dp__calendar_item:not(.dp__cell_disabled):not(.dp__cell_offset)"
    ].join(", ")

    const validationMessages = {
      required: "This field is required"
    }
    const validations = {
      form: {
        selectedDetention: {
          required: helpers.withMessage(validationMessages.required, required)
        },
        detentionDate: {
          required: helpers.withMessage(validationMessages.required, required)
        }
      }
    }
    const v$ = useVuelidate(validations.form, state.form)

    const isFormValid = computed(() => !v$.value.$invalid)

    const isUpdateMode = computed(() => Boolean(props.editableDetention))

    const errors = computed(() => {
      const errorObj = {}
      v$.value.$errors.forEach((err) => {
        errorObj[err.$property] = err.$message
      })

      return errorObj
    })

    const setFormData = (data) => {
      state.form = Object.assign(state.form, data)
    }

    const resetFormData = () => {
      setFormData({
        selectedDetention: null,
        detentionDate: null
      })
      v$.value.$reset()
    }
    const setResponseInfoBox = (title, message) => {
      if (title || message) {
        state.serverRes = {
          message,
          title
        }
      } else {
        state.serverRes = null
      }
    }
    const submit = (isUpdate) => {
      if (isFormValid.value) {
        const data = { dates: [] }
        if (state.form.detentionDate.length) {
          state.form.detentionDate.forEach((date) => {
            data.dates.push({
              schedule_date: helpersJS.currTzDate(date, "MM/DD/YYYY"),
              detention_id: state.form.selectedDetention.id
            })
          })
        } else {
          data.dates.push({
            schedule_date: helpersJS.currTzDate(
              state.form.detentionDate,
              "MM/DD/YYYY"
            ),
            detention_id: state.form.selectedDetention.id
          })
        }
        if (isUpdate) {
          updateScheduledDetention({
            dateId: state.form.selectedDetention.date_id,
            data: data
          })
        } else {
          scheduleDetention(data)
        }
      } else {
        v$.value.$touch()
      }
    }

    const scheduleDetention = (data) => {
      state.isProcessing = true
      store
        .dispatch("detentions/scheduleDetention", data)
        .then(() => {
          resetFormData()
          setResponseInfoBox("Success", "Successfully created!")
          state.isProcessing = false
          state.serverErrors = {}
          setTimeout(() => {
            setResponseInfoBox()
            emit("cancel")
          }, 1800)
        })
        .catch((err) => {
          const res = err.response.data
          state.serverErrors = res.errors ? res.errors : {}
          setResponseInfoBox("Error", res.message)
          state.isProcessing = false
        })
    }
    const updateScheduledDetention = (data) => {
      state.isProcessing = true
      store
        .dispatch("detentions/updateScheduledDetention", data)
        .then(() => {
          setResponseInfoBox("Success", "Successfully updated!")
          state.isProcessing = false
          state.serverErrors = {}
          setTimeout(() => {
            setResponseInfoBox()
            emit("cancel")
          }, 1800)
        })
        .catch((err) => {
          const res = err.response.data
          state.serverErrors = res.errors ? res.errors : {}
          setResponseInfoBox("Error", res.message)
          state.isProcessing = false
        })
    }
    const onCancel = () => {
      resetFormData()
      emit("cancel")
    }

    const getDetentions = () => {
      store
        .dispatch("detentions/getDetentionsCached")
        .then((response) => {
          state.detentions = response.data.data.map((detention) => ({
            ...detention,
            label: detention.name,
            value: detention.id
          }))

          // if we schedule detention from a day selected form we need to filter the detentions
          if (props.dailyDetentionsIds) {
            state.detentions = state.detentions.filter((detention) => {
              return !props.dailyDetentionsIds.includes(detention.id)
            })
          }
        })

        .catch((err) => {
          const res = err.response.data
          state.serverErrors = res.errors ? res.errors : {}
          setResponseInfoBox("Error", res.message)
          state.isProcessing = false
        })
    }

    const bulkDetentionDatePickerRange = ref(null)
    const bulkDetentionDatePickerMulti = ref(null)
    const lastBulkCalendarFocusSelector = ref("")
    const shouldRestoreBulkCalendarFocus = ref(false)
    let bulkCalendarObserver = null

    const escapeAttrValue = (value) =>
      String(value || "")
        .replace(/\\/g, "\\\\")
        .replace(/"/g, '\\"')

    const getActiveBulkDatePickerInstance = () =>
      state.isRangeModeEnabled
        ? bulkDetentionDatePickerRange.value
        : bulkDetentionDatePickerMulti.value

    const getActiveBulkDatePickerRoot = () => {
      const pickerEl = getActiveBulkDatePickerInstance()?.$el
      if (!pickerEl?.querySelector) return null
      return (
        pickerEl.querySelector(CALENDAR_ROOT_SELECTOR) ||
        pickerEl.querySelector(CALENDAR_ROOT_FALLBACK_SELECTOR) ||
        pickerEl
      )
    }

    const setMonthYearAccessibleNames = (rootEl) => {
      if (!rootEl?.querySelector) return

      const monthToggle = rootEl.querySelector(CALENDAR_MONTH_TOGGLE_SELECTOR)
      if (monthToggle) {
        const monthText = monthToggle.textContent?.trim() || "Month"
        monthToggle.setAttribute(
          "aria-label",
          `${monthText} selected. Open months overlay`
        )
      }

      const yearToggle = rootEl.querySelector(CALENDAR_YEAR_TOGGLE_SELECTOR)
      if (yearToggle) {
        const yearText = yearToggle.textContent?.trim() || "Year"
        yearToggle.setAttribute(
          "aria-label",
          `${yearText} selected. Open years overlay`
        )
      }
    }

    const patchCalendarHeaders = (rootEl) => {
      if (!rootEl?.querySelectorAll) return
      const headerRow = rootEl.querySelector(CALENDAR_HEADER_SELECTOR)
      if (headerRow) {
        headerRow.setAttribute("role", "row")
      }
      const headerItems = rootEl.querySelectorAll(CALENDAR_HEADER_ITEM_SELECTOR)
      headerItems.forEach((item) => {
        item.setAttribute("role", "columnheader")
      })
    }

    const removeDisabledAndOffsetDaysFromTabFlow = (rootEl) => {
      if (!rootEl?.querySelectorAll) return
      const blockedNodes = rootEl.querySelectorAll(
        CALENDAR_DISABLED_OR_OFFSET_SELECTOR
      )
      blockedNodes.forEach((node) => {
        if (!node) return
        node.setAttribute("tabindex", "-1")
        if (
          node.classList?.contains?.("dp__cell_disabled") ||
          node.getAttribute("aria-disabled") === "true"
        ) {
          node.setAttribute("aria-disabled", "true")
        }
        if (node.classList?.contains?.("dp__cell_offset")) {
          node.setAttribute("aria-hidden", "true")
        }
      })
    }

    const keepMonthYearControlsFocusable = (rootEl) => {
      if (!rootEl?.querySelectorAll) return
      const navAndToggleButtons = rootEl.querySelectorAll(
        CALENDAR_NAV_AND_TOGGLE_SELECTOR
      )
      navAndToggleButtons.forEach((button) => {
        if (button?.closest?.("[aria-hidden='true']")) return
        button.setAttribute("tabindex", "0")
      })
    }

    /*
     * Offset/hidden day cells (e.g. with `hide-offset-dates`) render an empty
     * `.dp__cell_inner` (`<div class="dp__cell_inner"><!----></div>`) that still sits in
     * the tab/arrow order, so focus lands on an empty, invisible element (Deque 2175475).
     *
     * `tabindex="-1"` alone is NOT enough: it only removes the cell from the Tab
     * sequence, but vue-datepicker's arrow-key navigation focuses cells via a
     * programmatic `element.focus()`, which still works on a `tabindex="-1"`
     * element. To make an empty cell truly non-focusable while keeping the grid
     * layout intact (so real days stay in the correct weekday column), we also
     * set `visibility: hidden` — a non-rendered element cannot receive focus, so
     * `.focus()` becomes a no-op and the focus ring never appears on a blank box.
     * Cells that later gain a day number (after month navigation / re-render) are
     * restored.
     */
    const untabEmptyDayCells = (rootEl) => {
      if (!rootEl?.querySelectorAll) return
      const innerCells = rootEl.querySelectorAll(CALENDAR_DAY_INNER_SELECTOR)
      innerCells.forEach((cell) => {
        if (!cell) return
        const hasText = (cell.textContent || "").trim().length > 0
        if (hasText) {
          if (cell.dataset.a11yEmptyHidden === "true") {
            cell.style.visibility = ""
            cell.removeAttribute("aria-hidden")
            delete cell.dataset.a11yEmptyHidden
          }
          return
        }
        cell.setAttribute("tabindex", "-1")
        cell.setAttribute("aria-hidden", "true")
        cell.style.visibility = "hidden"
        cell.dataset.a11yEmptyHidden = "true"
      })
    }

    const applyBulkCalendarA11yPatches = () => {
      const root = getActiveBulkDatePickerRoot()
      if (!root) return
      patchCalendarHeaders(root)
      setMonthYearAccessibleNames(root)
      removeDisabledAndOffsetDaysFromTabFlow(root)
      untabEmptyDayCells(root)
      keepMonthYearControlsFocusable(root)
    }

    const getCalendarFocusSelectorFromTarget = (target) => {
      if (!target?.closest?.(".bulk-schedule-calendar")) return ""
      const dataTestId = target.getAttribute?.("data-test")
      if (dataTestId) {
        return `[data-test="${escapeAttrValue(dataTestId)}"]`
      }
      const ariaLabel = target.getAttribute?.("aria-label")
      if (ariaLabel && target.matches?.("button,[role='button']")) {
        return `button[aria-label="${escapeAttrValue(ariaLabel)}"], [role='button'][aria-label="${escapeAttrValue(ariaLabel)}"]`
      }
      if (target.matches?.(CALENDAR_DAY_SELECTOR)) {
        return `${CALENDAR_DAY_SELECTOR}:not(.dp__cell_disabled):not(.dp__cell_offset)`
      }
      const inner = target.closest?.(CALENDAR_DAY_INNER_SELECTOR)
      if (inner) {
        return `${CALENDAR_DAY_INNER_SELECTOR}:not(.dp__cell_disabled):not(.dp__cell_offset)`
      }
      return ""
    }

    const onBulkCalendarFocusIn = (event) => {
      const selector = getCalendarFocusSelectorFromTarget(event?.target)
      if (selector) {
        lastBulkCalendarFocusSelector.value = selector
      }
    }

    const restoreBulkCalendarFocus = () => {
      const root = getActiveBulkDatePickerRoot()
      if (!root) return

      const activeElement = document.activeElement
      if (activeElement?.closest?.(".bulk-schedule-calendar")) return

      const requestedTarget = lastBulkCalendarFocusSelector.value
        ? root.querySelector(lastBulkCalendarFocusSelector.value)
        : null
      const fallbackTarget =
        requestedTarget ||
        root.querySelector(CALENDAR_ENABLED_FOCUS_TARGET_SELECTOR)
      fallbackTarget?.focus?.({ preventScroll: true })
    }

    const disconnectBulkCalendarObserver = () => {
      if (bulkCalendarObserver) {
        bulkCalendarObserver.disconnect()
        bulkCalendarObserver = null
      }
    }

    const setupBulkCalendarObserver = () => {
      disconnectBulkCalendarObserver()
      const root = getActiveBulkDatePickerRoot()
      if (!root || typeof MutationObserver === "undefined") return
      bulkCalendarObserver = new MutationObserver(() => {
        applyBulkCalendarA11yPatches()
      })
      bulkCalendarObserver.observe(root, { subtree: true, childList: true })
    }

    const onChangeSelectedDetention = (detention) => {
      state.selectedDetention = detention
      state.form.detentionDate = props.selectedDate
        ? new Date(props.selectedDate)
        : null
      if (state.isRangeModeEnabled) {
        bulkDetentionDatePickerRange.value?.clearValue()
      } else {
        bulkDetentionDatePickerMulti.value?.clearValue()
      }
    }

    const tomorrow = computed(() => {
      return moment().add(1, "days").format("MM/DD/YYYY")
    })

    const maxSchoolYearDate = computed(() => {
      return helpersJS.maxSchoolYearDate()
    })

    const detentionsCalendarData = computed(
      () => store.getters["detentions/detentionsCalendarDataBulkForm"]
    )

    const disabledDates = computed(() => {
      return state.form.selectedDetention
        ? detentionsCalendarData.value
            .filter(
              (detention) => detention.id === state.form.selectedDetention.id
            )
            .map((detention) =>
              moment(detention.date).utc().format("MM/DD/YYYY")
            )
        : []
    })

    const getDatesInRange = (startDate, endDate) => {
      const date = new Date(startDate.getTime())
      const dates = []
      while (date <= endDate) {
        dates.push(new Date(date))
        date.setDate(date.getDate() + 1)
      }
      return dates
    }

    const handleSelectedDates = (modelData) => {
      if (modelData?.length) {
        state.form.detentionDate = state.isRangeModeEnabled
          ? getDatesInRange(modelData[0], modelData[1])
          : modelData
      }
    }

    const handleMonthChange = ({ month, year }) => {
      const activeElement = document.activeElement
      const selector = getCalendarFocusSelectorFromTarget(activeElement)
      if (selector) {
        lastBulkCalendarFocusSelector.value = selector
        shouldRestoreBulkCalendarFocus.value = true
      } else {
        shouldRestoreBulkCalendarFocus.value = false
      }
      const start = moment({ year, month })
        .startOf("month")
        .format("YYYY-MM-DD")
      const end = moment({ year, month }).endOf("month").format("YYYY-MM-DD")
      state.isDetentionDataLoading = true

      getDetentionCalendarData(start, end)
    }

    const resetMonthToCurrent = () => {
      handleMonthChange({
        instance: null,
        month: currentMonth,
        year: currentYear
      })
    }

    const getDetentionCalendarData = (startDate, endDate) => {
      const params = {
        startDate: startDate,
        endDate: endDate
      }
      store.dispatch("detentions/getDetentionsCalendarDataBulkForm", params)
    }

    const onDelete = () => {
      actionDialog.open(deleteDetention, {
        props: { danger: true, title: "Delete", question: "Are you sure?" }
      })
    }
    const deleteDetention = () => {
      state.isProcessing = true
      store
        .dispatch(
          "detentions/deleteScheduledDetention",
          props.editableDetention.date_id
        )
        .then(() => {
          setResponseInfoBox("Success", "Successfully deleted!")
          state.isProcessing = false
          state.serverErrors = {}
          setTimeout(() => {
            setResponseInfoBox()
            emit("cancel")
          }, 1800)
        })
        .catch((err) => {
          const res = err.response.data
          state.serverErrors = res.errors ? res.errors : {}
          setResponseInfoBox("Error", res.message)
          state.isProcessing = false
        })
        .finally(() => {
          state.isProcessing = false
        })
    }
    watch(
      () => state.isBulkAddEnabled,
      (bulkAddIsEnabled) => {
        if (!bulkAddIsEnabled) {
          state.form.detentionDate = props.selectedDate
            ? new Date(props.selectedDate)
            : null
          shouldRestoreBulkCalendarFocus.value = false
          disconnectBulkCalendarObserver()
        } else {
          resetMonthToCurrent()
          nextTick(() => {
            applyBulkCalendarA11yPatches()
            setupBulkCalendarObserver()
          })
        }
      }
    )
    watch(
      () => state.isRangeModeEnabled,
      () => {
        state.form.detentionDate = null
        shouldRestoreBulkCalendarFocus.value = false
        resetMonthToCurrent()
        nextTick(() => {
          applyBulkCalendarA11yPatches()
          setupBulkCalendarObserver()
        })
      }
    )
    watch(detentionsCalendarData, () => {
      state.isDetentionDataLoading = false
      nextTick(() => {
        applyBulkCalendarA11yPatches()
        setupBulkCalendarObserver()
        if (shouldRestoreBulkCalendarFocus.value) {
          restoreBulkCalendarFocus()
          shouldRestoreBulkCalendarFocus.value = false
        }
      })
    })
    onUnmounted(() => {
      disconnectBulkCalendarObserver()
    })
    return {
      state,
      v$,
      submit,
      isFormValid,
      errors,
      isUpdateMode,
      onCancel,
      getDetentions,
      onChangeSelectedDetention,
      onDelete,
      tomorrow,
      maxSchoolYearDate,
      disabledDates,
      handleSelectedDates,
      handleMonthChange,
      bulkDetentionDatePickerRange,
      bulkDetentionDatePickerMulti,
      onBulkCalendarFocusIn
    }
  }
}
</script>
<style lang="scss">
.dp__outer_menu_wrap {
  .dp__instance_calendar {
    font-weight: 400;
  }
  .dp__calendar_item {
    display: flex;
    justify-content: center;
  }
}
.bulk-schedule-calendar div:has(.dp__outer_menu_wrap) {
  width: 100%;
}
</style>
